import { Button } from "#components/ui/button"
import { Calendar } from "#components/ui/calendar"
import { Checkbox } from "#components/ui/checkbox"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "#components/ui/collapsible"
import { Input } from "#components/ui/input"
import { Label } from "#components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "#components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#components/ui/select"
import { useI18n } from "#hooks/use-i18n"
import { getDateLocale } from "#lib/formatters"
import type { ExpenseCategory } from "@home-finance-manager/shared/types/expense_categories"
import type { Partner } from "@home-finance-manager/shared/types/partners"
import { format } from "date-fns"
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react"
import * as React from "react"
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from "react-hook-form"
import type { ExpenseFormData } from "./expense-form-schema"

interface ExpenseFormProps {
  control: Control<ExpenseFormData>
  errors: FieldErrors<ExpenseFormData>
  categories: ExpenseCategory[]
  recurringValue?: "one-time" | "monthly" | "quarterly" | "yearly" | "weekly" | null
  partners: Partner[]
  watch: UseFormWatch<ExpenseFormData>
  setValue: UseFormSetValue<ExpenseFormData>
}

const formatAmountValue = (value: number | string | undefined): string => {
  if (typeof value === "number") {
    return value === 0 ? "" : String(value)
  }
  return value || ""
}

const getPartnerName = (partner: Partner): string => {
  const fullName = `${partner.firstName || ""} ${partner.lastName || ""}`.trim()
  return fullName || partner.email || `Partner ${partner.id}`
}

const createSplitsForPartners = (
  partners: Partner[],
  preference: "equitable" | "equal" | "custom"
): Array<{
  partnerId: number
  splitType: "custom"
  customPercentage: number
}> => {
  if (preference !== "custom") {
    return []
  }
  return partners.map((partner) => ({
    partnerId: partner.id,
    splitType: "custom" as const,
    customPercentage: 0,
  }))
}
interface DatePickerFieldProps {
  field: {
    value?: string
    onChange: (value: string) => void
  }
  error?: string
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({ field, error }) => {
  const { t } = useI18n()
  const [open, setOpen] = React.useState(false)

  const dateValue = React.useMemo(() => {
    if (!field.value) return undefined
    try {
      const date = new Date(field.value)
      return isNaN(date.getTime()) ? undefined : date
    } catch {
      return undefined
    }
  }, [field.value])

  const displayValue = React.useMemo(() => {
    const dateLocale = getDateLocale()
    return dateValue
      ? format(dateValue, "PPP", { locale: dateLocale })
      : t("components.expenses.form.pickDate")
  }, [dateValue, t])

  const handleDateSelect = React.useCallback(
    (date: Date | undefined) => {
      if (date) {
        field.onChange(date.toISOString())
        setOpen(false)
      } else {
        field.onChange(new Date().toISOString())
      }
    },
    [field]
  )

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full min-w-0 max-w-full justify-start text-left font-normal"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateValue ? displayValue : <span>{t("components.expenses.form.pickDate")}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={dateValue} onSelect={handleDateSelect} initialFocus />
        </PopoverContent>
      </Popover>
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </>
  )
}

interface CustomPercentageInputProps {
  partner: Partner
  value: number | null | undefined
  onChange: (value: number) => void
  onBlur: () => void
  error?: string
}

const CustomPercentageInput: React.FC<CustomPercentageInputProps> = ({
  partner,
  value,
  onChange,
  onBlur,
  error,
}) => {
  const partnerName = React.useMemo(() => getPartnerName(partner), [partner])

  const displayValue = React.useMemo(() => {
    return value === null || value === undefined ? "" : String(value)
  }, [value])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const numValue = inputValue === "" ? 0 : parseFloat(inputValue) || 0
      onChange(numValue)
    },
    [onChange]
  )

  return (
    <div className="space-y-2 p-4 border rounded-lg">
      <Label htmlFor={`custom-percentage-${partner.id}`} className="text-sm font-medium">
        {partnerName}
      </Label>
      <div className="space-y-1">
        <div className="relative">
          <Input
            id={`custom-percentage-${partner.id}`}
            type="number"
            min={0}
            max={100}
            step="0.01"
            className="w-full min-w-0 max-w-full pr-8"
            value={displayValue}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder="0.00"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground text-sm">
            %
          </span>
        </div>
        {error && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    </div>
  )
}

interface CustomSplitsSectionProps {
  partners: Partner[]
  control: Control<ExpenseFormData>
  errors: FieldErrors<ExpenseFormData>
  watch: UseFormWatch<ExpenseFormData>
  setValue: UseFormSetValue<ExpenseFormData>
}

const CustomSplitsSection: React.FC<CustomSplitsSectionProps> = ({
  partners,
  control,
  errors,
  watch,
  setValue,
}) => {
  const watchedSplits =
    useWatch({
      control,
      name: "splits",
      defaultValue: [],
    }) || []

  const totalPercentage = watchedSplits
    .filter((s) => s?.splitType === "custom")
    .reduce((sum, s) => sum + (Number(s?.customPercentage) || 0), 0)

  const isTotalValid = Math.abs(totalPercentage - 100) <= 0.01

  const partnerSplits = React.useMemo(() => {
    return partners.map((partner) => {
      const splitIndex = watchedSplits.findIndex((s) => s?.partnerId === partner.id)
      return {
        partner,
        splitIndex: splitIndex >= 0 ? splitIndex : partners.indexOf(partner),
      }
    })
  }, [partners, watchedSplits])

  const { t } = useI18n()

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("components.expenses.form.splitInstructions")}
      </p>
      {partnerSplits.map(({ partner, splitIndex }) => {
        const fieldName =
          splitIndex >= 0
            ? (`splits.${splitIndex}.customPercentage` as const)
            : (`splits.${partners.indexOf(partner)}.customPercentage` as const)

        return (
          <Controller
            key={partner.id}
            name={fieldName}
            control={control}
            defaultValue={0}
            render={({ field }) => {
              const handleChange = (value: number) => {
                const currentSplits = watch("splits") || []
                const updatedSplits = [...currentSplits]
                const idx = updatedSplits.findIndex((s) => s?.partnerId === partner.id)

                if (idx >= 0) {
                  updatedSplits[idx] = {
                    ...updatedSplits[idx],
                    partnerId: partner.id,
                    splitType: "custom",
                    customPercentage: value,
                  }
                } else {
                  updatedSplits.push({
                    partnerId: partner.id,
                    splitType: "custom",
                    customPercentage: value,
                  })
                }

                setValue("splits", updatedSplits, { shouldValidate: true, shouldDirty: true })
                field.onChange(value)
              }

              return (
                <CustomPercentageInput
                  partner={partner}
                  value={field.value}
                  onChange={handleChange}
                  onBlur={field.onBlur}
                  error={errors.splits?.[splitIndex]?.customPercentage?.message}
                />
              )
            }}
          />
        )
      })}
      <div className="text-sm">
        <span className={isTotalValid ? "text-muted-foreground" : "text-destructive"}>
          {t("components.expenses.form.totalPercentage")}: {totalPercentage.toFixed(2)}%
        </span>
        {!isTotalValid && (
          <span className="text-destructive ml-2">
            {t("components.expenses.form.totalMustEqual100")}
          </span>
        )}
      </div>
    </div>
  )
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  control,
  errors,
  categories,
  recurringValue,
  partners,
  watch,
  setValue,
}) => {
  const { t } = useI18n()
  const splitPreference =
    useWatch({
      control,
      name: "splitPreference",
      defaultValue: "equitable",
    }) || "equitable"
  const currentSplits =
    useWatch({
      control,
      name: "splits",
      defaultValue: [],
    }) || []
  const showCustomSplits = splitPreference === "custom"

  React.useEffect(() => {
    if (splitPreference !== "custom") {
      if (currentSplits.length > 0) {
        setValue("splits", [], { shouldValidate: false })
      }
      return
    }

    if (partners.length > 0 && currentSplits.length === 0) {
      const splits = createSplitsForPartners(partners, "custom")
      setValue("splits", splits, { shouldValidate: false })
    } else if (partners.length > 0 && currentSplits.length > 0) {
      const splitPartnerIds = currentSplits.map((s) => s.partnerId)
      const missingPartners = partners.filter((p) => !splitPartnerIds.includes(p.id))
      if (missingPartners.length > 0) {
        const newSplits = createSplitsForPartners(missingPartners, "custom")
        setValue("splits", [...currentSplits, ...newSplits], { shouldValidate: false })
      }
    }
  }, [partners.length, currentSplits.length, splitPreference, setValue, currentSplits])

  const handleAmountChange = React.useCallback(
    (field: { onChange: (value: number) => void }) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = e.target.value
      field.onChange(parsed as unknown as number)
    },
    []
  )

  const handleAmountValue = React.useCallback(
    (value: number | string | undefined) => formatAmountValue(value),
    []
  )

  const handleCategoryChange = React.useCallback(
    (field: { onChange: (value: number | undefined) => void }) => (value: string) => {
      const parsed = parseInt(value)
      field.onChange(isNaN(parsed) ? undefined : parsed)
    },
    []
  )

  const handleSplitPreferenceChange = React.useCallback(
    (field: { onChange: (value: "equitable" | "equal" | "custom") => void }) =>
      (value: "equitable" | "equal" | "custom") => {
        field.onChange(value)
        if (value === "custom") {
          const splits = createSplitsForPartners(partners, "custom")
          setValue("splits", splits)
        } else {
          setValue("splits", [], { shouldValidate: false })
        }
      },
    [partners, setValue]
  )

  const showDatePicker = React.useMemo(() => {
    return recurringValue && recurringValue !== "one-time"
  }, [recurringValue])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2 min-w-0 px-3">
          <Label htmlFor="name">Name *</Label>
          <div className="relative min-w-0 w-full">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input {...field} id="name" className="w-full min-w-0 max-w-full" />
              )}
            />
          </div>
          {errors.name && <p className="text-sm text-destructive mt-1">{errors.name.message}</p>}
        </div>
        <div className="space-y-2 min-w-0 px-3">
          <Label htmlFor="amount">Amount *</Label>
          <div className="relative min-w-0 w-full">
            <Controller
              name={"amount" as any}
              control={control}
              render={({ field }) => (
                <Input
                  id="amount"
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-full min-w-0 max-w-full"
                  value={handleAmountValue(field.value as number)}
                  onChange={handleAmountChange(field)}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
            />
          </div>
          {errors.amount && (
            <p className="text-sm text-destructive mt-1">{errors.amount.message}</p>
          )}
        </div>
      </div>
      <div className="space-y-2 min-w-0 px-3">
        <Label htmlFor="description">{t("components.expenses.form.descriptionLabel")}</Label>
        <div className="relative min-w-0 w-full">
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ""}
                id="description"
                placeholder={t("components.expenses.form.descriptionPlaceholder")}
                className="w-full min-w-0 max-w-full"
              />
            )}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2 min-w-0 px-3">
          <Label htmlFor="expenseCategoryId">{t("components.expenses.form.categoryLabel")}</Label>
          <div className="relative min-w-0 w-full">
            <Controller
              name="expenseCategoryId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ? field.value.toString() : ""}
                  onValueChange={handleCategoryChange(field)}
                >
                  <SelectTrigger id="expenseCategoryId" className="w-full min-w-0 max-w-full">
                    <SelectValue placeholder={t("components.expenses.form.categoryPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.length === 0 && (
                      <SelectItem value="none">
                        {t("components.expenses.form.noCategories")}
                      </SelectItem>
                    )}
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          {errors.expenseCategoryId && (
            <p className="text-sm text-destructive mt-1">{errors.expenseCategoryId.message}</p>
          )}
        </div>
        <div className="space-y-2 min-w-0 px-3">
          <Label htmlFor="recurring">{t("components.expenses.form.recurringLabel")}</Label>
          <div className="relative min-w-0 w-full">
            <Controller
              name="recurring"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="recurring" className="w-full min-w-0 max-w-full">
                    <SelectValue placeholder={t("components.expenses.form.recurringPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">
                      {t("components.expenses.form.recurringOneTime")}
                    </SelectItem>
                    <SelectItem value="weekly">
                      {t("components.expenses.form.recurringWeekly")}
                    </SelectItem>
                    <SelectItem value="monthly">
                      {t("components.expenses.form.recurringMonthly")}
                    </SelectItem>
                    <SelectItem value="quarterly">
                      {t("components.expenses.form.recurringQuarterly")}
                    </SelectItem>
                    <SelectItem value="yearly">
                      {t("components.expenses.form.recurringYearly")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>
      </div>
      {showDatePicker && (
        <div className="space-y-2 min-w-0 px-3">
          <Label htmlFor="nextPaymentDate">Next Payment Date *</Label>
          <div className="relative min-w-0 w-full">
            <Controller
              name="nextPaymentDate"
              control={control}
              render={({ field }) => (
                <DatePickerField
                  field={{
                    value: typeof field.value === "string" ? field.value : undefined,
                    onChange: field.onChange,
                  }}
                  error={errors.nextPaymentDate?.message}
                />
              )}
            />
          </div>
          {errors.nextPaymentDate && (
            <p className="text-sm text-destructive mt-1">{errors.nextPaymentDate.message}</p>
          )}
        </div>
      )}
      <div className="px-3 space-y-3">
        <Controller
          name="isVirtual"
          control={control}
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox id="isVirtual" checked={field.value} onCheckedChange={field.onChange} />
              <Label htmlFor="isVirtual">{t("components.expenses.form.virtualExpenseLabel")}</Label>
            </div>
          )}
        />
        <Controller
          name="isIncome"
          control={control}
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox id="isIncome" checked={field.value} onCheckedChange={field.onChange} />
              <Label htmlFor="isIncome">{t("components.expenses.form.markAsIncomeLabel")}</Label>
            </div>
          )}
        />
      </div>

      {partners.length > 0 && (
        <Collapsible defaultOpen={false}>
          <div className="space-y-2">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity">
                <Label className="text-base font-semibold">
                  {t("components.expenses.form.expenseSplitsLabel")}
                </Label>
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  {t("components.expenses.form.expenseSplitsDescription")}
                </p>

                <div className="space-y-2">
                  <Label htmlFor="split-preference">
                    {t("components.expenses.form.repartitionPreferenceLabel")}
                  </Label>
                  <Controller
                    name="splitPreference"
                    control={control}
                    defaultValue="equitable"
                    render={({ field }) => (
                      <Select
                        value={field.value || "equitable"}
                        onValueChange={handleSplitPreferenceChange(field)}
                      >
                        <SelectTrigger id="split-preference" className="w-full min-w-0 max-w-full">
                          <SelectValue
                            placeholder={t("components.expenses.form.repartitionPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equitable">
                            {t("components.expenses.form.equitableSplit")}
                          </SelectItem>
                          <SelectItem value="equal">
                            {t("components.expenses.form.equalSplit")}
                          </SelectItem>
                          <SelectItem value="custom">
                            {t("components.expenses.form.customSplit")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {showCustomSplits && (
                  <CustomSplitsSection
                    partners={partners}
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                  />
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}
    </div>
  )
}
